import { findIndex } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import {
  CallSessionDto,
  FindAllSpeakToTextDto,
  GetOmniSpeakToTextResponseDto,
  SpeakToTextDto,
  SpeakToTextPosition
} from 'types';
import { calculateStartEndPositionSpeakToText } from 'utils';
import { useReadOmniChatMessagesMutation } from 'services';
import { QueryActionCreatorResult } from '@reduxjs/toolkit/query';
import { useReceiveOmniSpeakToText } from './useReceiveOmniSpeakToText';

type PropsType = {
  callSession: CallSessionDto;
  fetchMessage: (value: FindAllSpeakToTextDto) => QueryActionCreatorResult<any>;
  hasNew?: boolean;
  setLoading: (value: boolean) => void;
  isRead?: boolean;
  maxMessageWidth?: number;
  isShowName?: boolean;
};

export function useScrollOmniSpeakToText({
  callSession,
  fetchMessage,
  hasNew,
  setLoading,
  isRead = true,
  maxMessageWidth = 300,
  isShowName = true
}: PropsType) {
  const { callSessionId } = callSession;
  const [isAtBottomScroll, setIsAtBottomScroll] = useState(true);
  const [displayIndex, setDisplayIndex] = useState({ start: 0, end: 50 });

  const messagesBoxRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const positionsRef = useRef<SpeakToTextPosition[]>([]);

  const [onReadMess] = useReadOmniChatMessagesMutation();

  const { handleReceiveOlderOmniSpeakToTextList } = useReceiveOmniSpeakToText(maxMessageWidth, isShowName);

  useEffect(() => {
    const div = messagesBoxRef?.current;
    if (div) {
      div.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (div) {
        div.removeEventListener('scroll', handleScroll);
      }
    };
  }, [positionsRef.current, messagesBoxRef]);

  // const handleReadAllMess = (data: ReadOmniChannelMessageDto) => {
  //   if (isRead) onReadMess(data).unwrap();
  // };

  const changeHeightChatList = (height: number, scrollTop?: number) => {
    if (messageListRef.current) {
      messageListRef.current.style.height = `${height}px`;
    }
    if (messagesBoxRef.current && scrollTop) {
      messagesBoxRef.current.scrollTop = scrollTop;
    }
  };

  const handleScroll = () => {
    if (!messagesBoxRef?.current) return;
    const scrollTop = messagesBoxRef.current.scrollTop;
    const clientHeight = messagesBoxRef.current.clientHeight;
    const scrollHeight = messagesBoxRef.current.scrollHeight;
    setIsAtBottomScroll(scrollTop > scrollHeight - 2 * clientHeight);
    setDisplayIndex(
      calculateStartEndPositionSpeakToText(
        {
          scrollTop,
          clientHeight
        },
        positionsRef.current
      )
    );
  };

  const scrollToMessage = (repliedMessage: SpeakToTextDto, color?: string) => {
    const replyMessIdx = findIndex(positionsRef.current, (o) => o.speakToTextId === repliedMessage.speakToTextId);
    if (replyMessIdx === -1) return;
    if (!messageListRef.current) return;

    const messReplied = positionsRef.current[replyMessIdx];
    messagesBoxRef.current?.scrollTo({
      top: (messReplied?.position ?? 0) - (messReplied?.height > 100 ? 0 : 100),
      behavior: 'smooth'
    });
    // maskedMessageReplied(messReplied, messageListRef.current, color, () => setLoading(false));
  };

  const scrollToEndMessage = () => {
    if (hasNew) {
      setLoading(true);
      if (messageListRef.current?.offsetHeight) {
        messagesBoxRef?.current?.scrollTo({
          top: messageListRef.current?.offsetHeight,
          behavior: 'smooth'
        });
      }
      fetchMessage({
        callSessionId,
        startMessageId: undefined
      })
        .unwrap()
        .then(async (rs) => {
          const response = rs as GetOmniSpeakToTextResponseDto;

          const data = await handleReceiveOlderOmniSpeakToTextList(response.data.rows, null, []);
          if (data) {
            changeHeightChatList(data.height);
            positionsRef.current = data.positions;
            messagesBoxRef?.current?.scrollTo({
              top: data.height,
              behavior: 'smooth'
            });
          }
          // handleReadAllMess({
          //   callSessionId,
          //   messageIds: []
          // });
        })
        .finally(() => {
          setTimeout(() => {
            setLoading(false);
          }, 500);
        });
    } else {
      if (messagesBoxRef.current) {
        messagesBoxRef?.current?.scrollTo({
          top: messagesBoxRef.current?.scrollHeight + 200,
          behavior: 'smooth'
        });
      }
    }
  };

  return {
    messagesBoxRef,
    messageListRef,
    positionsRef,
    isAtBottomScroll,
    displayIndex,
    scrollToEndMessage,
    changeHeightChatList
  };
}
